import { RouteFactory } from "@app/routePaths";
import type { NavigationItem } from "@app/types/Navigation";
import dayjs from "dayjs";

export const SPH_SERVICE = import.meta.env.VITE_SPH_SVC;
const LIVE_ENVIRONMENTS = ["development-env", "staging-env", "production-env"];
export const ENVIRONMENT: string = import.meta.env.VITE_ENVIRONMENT;
export const isLocalDevOrStg = [
  "local",
  "development-env",
  "staging-env",
].includes(ENVIRONMENT);
export const isLocal = ENVIRONMENT === "local";
export const getEnvironment = () => ENVIRONMENT;

export const IS_PLAYWRIGHT =
  String(import.meta.env.VITE_IS_PLAYWRIGHT).toLowerCase() === "true";
export const PLAYWRIGHT_DATE = "March 18, 2026";
export const isLiveEnvironment = LIVE_ENVIRONMENTS.includes(ENVIRONMENT);
export const PROD_URL = "www.businesstimes.com.sg";

export const gtmId = "GTM-WH9XNQW";
export const DRUPAL_API_ENDPOINT = import.meta.env.VITE_DRUPAL_API_ENDPOINT;
export const EPAPER_API_ENDPOINT = import.meta.env.VITE_EPAPER_API_ENDPOINT;
export const LIVE_RAMP_PID = import.meta.env.VITE_LIVE_RAMP_PID;

export const CDP_URL: string = import.meta.env.VITE_CDP_URL;
export const CDP_ACTIVATION_URL: string = import.meta.env
  .VITE_CDP_ACTIVATION_URL;
export const CDP_HASH: string = import.meta.env.VITE_CDP_HASH;
export const CDP_EXIT_INTERVENTION_MODULE_ID = import.meta.env
  .VITE_CDP_EXIT_INTERVENTION_MODULE_ID;
export const CDP_PAYWALL_MODULE_ID = import.meta.env.VITE_CDP_PAYWALL_MODULE_ID;
export const CDP_PAYWALL_180_DAYS_MODULE_ID = import.meta.env
  .VITE_CDP_PAYWALL_180_DAYS_MODULE_ID;
export const CDP_PAYWALL_30_DAYS_MODULE_ID = import.meta.env
  .VITE_CDP_PAYWALL_30_DAYS_MODULE_ID;
export const BT_BRIGHTCOVE_ACCOUNT_ID = "4802324426001";
export const MYBT_REG_LIMIT = 3;
export const MYBT_SUB_LIMIT = 30;

export const MYBT_EXCLUDE_KEYWORDS = ["spotlight"];
export const MYBT_ESHOP_SUBSCRIBE_CTA =
  "https://subscribe.sph.com.sg/publications-bt/?utm_campaign=bt_subscription&utm_medium=sph-publication&utm_source=bt&utm_content=mybt";
export const MYBT_API_URL = import.meta.env.VITE_MYBT_API_URL;
export const ESHOP_SUBSCRIBE_CTA_HEADER = import.meta.env
  .VITE_ESHOP_SUBSCRIBE_CTA_HEADER;
export const ESHOP_SUBSCRIBE_CTA_FOOTER = import.meta.env
  .VITE_ESHOP_SUBSCRIBE_CTA_FOOTER;
export const LIVE_DOMAIN = "https://www.businesstimes.com.sg";
export const WEBGRAPHIC_LIVE = "https://graphics.businesstimes.com.sg";
export const WEBGRAPHIC_STAGE = "https://graphics-uat.businesstimes.com.sg";
export const BUDGET2026_BANNER =
  "/bt_files/mobile_banner/budget2026/singapore-budget-2026.html";
export const BUDGET2026_KEYWORD_ID = 158604;

export const BT_API_URL = import.meta.env.VITE_BT_API_URL;
export const BT_URL = import.meta.env.VITE_BT_URL;

export const sectionNavigationItems: Record<string, NavigationItem> = {
  myBT: {
    key: "myBT",
    link: RouteFactory.myBT,
    label: "myBT",
  },
  singapore: {
    key: "singapore",
    link: RouteFactory.section("singapore"),
    label: "Singapore",
    children: ["singapore/economy-policy", "singapore/smes"],
  },
  "singapore/economy-policy": {
    key: "singapore/economy-policy",
    link: RouteFactory.section("singapore/economy-policy"),
    label: "Economy & Policy",
  },
  "singapore/smes": {
    key: "singapore/smes",
    link: RouteFactory.section("singapore/smes"),
    label: "SGSME",
  },
  international: {
    key: "international",
    link: RouteFactory.section("international"),
    label: "International",
    children: ["international/asean", "international/global"],
  },
  "international/asean": {
    key: "international/asean",
    link: RouteFactory.section("international/asean"),
    label: "Asean Business",
  },
  "international/global": {
    key: "international/global",
    link: RouteFactory.section("international/global"),
    label: "Global Enterprise",
  },
  "opinion-features": {
    key: "opinion-features",
    link: RouteFactory.section("opinion-features"),
    label: "Opinion & Features",
    stickyHeaderLabel: "Opinion",
    children: ["opinion-features/columns", "opinion-features/features"],
  },
  "opinion-features/columns": {
    key: "opinion-features/columns",
    link: RouteFactory.section("opinion-features/columns"),
    label: "Columns",
  },
  "opinion-features/features": {
    key: "opinion-features/features",
    link: RouteFactory.section("opinion-features/features"),
    label: "Features",
  },
  "companies-markets": {
    key: "companies-markets",
    link: RouteFactory.section("companies-markets"),
    label: "Companies & Markets",
    stickyHeaderLabel: "Companies",
    children: [
      "companies-markets/banking-finance",
      "companies-markets/reits-property",
      "companies-markets/energy-commodities",
      "companies-markets/telcos-media-tech",
      "companies-markets/transport-logistics",
      "companies-markets/consumer-healthcare",
      "companies-markets/capital-markets-currencies",
    ],
  },
  "companies-markets/banking-finance": {
    key: "companies-markets/banking-finance",
    link: RouteFactory.section("companies-markets/banking-finance"),
    label: "Banking & Finance",
  },
  "companies-markets/reits-property": {
    key: "companies-markets/reits-property",
    link: RouteFactory.section("companies-markets/reits-property"),
    label: "Reits & Property",
  },
  "companies-markets/energy-commodities": {
    key: "companies-markets/energy-commodities",
    link: RouteFactory.section("companies-markets/energy-commodities"),
    label: "Energy & Commodities",
  },
  "companies-markets/telcos-media-tech": {
    key: "companies-markets/telcos-media-tech",
    link: RouteFactory.section("companies-markets/telcos-media-tech"),
    label: "Telcos, Media & Tech",
  },
  "companies-markets/transport-logistics": {
    key: "companies-markets/transport-logistics",
    link: RouteFactory.section("companies-markets/transport-logistics"),
    label: "Transport & Logistics",
  },
  "companies-markets/consumer-healthcare": {
    key: "companies-markets/consumer-healthcare",
    link: RouteFactory.section("companies-markets/consumer-healthcare"),
    label: "Consumer & Healthcare",
  },
  "companies-markets/capital-markets-currencies": {
    key: "companies-markets/capital-markets-currencies",
    link: RouteFactory.section("companies-markets/capital-markets-currencies"),
    label: "Capital Markets & Currencies",
  },
  property: {
    key: "property",
    link: RouteFactory.section("property"),
    label: "Property",
    children: [
      "property/residential",
      "property/commercial-industrial",
      "property-supplements",
    ],
  },
  "property/residential": {
    key: "property/residential",
    link: RouteFactory.section("property/residential"),
    label: "Residential",
  },
  "property/commercial-industrial": {
    key: "property/commercial-industrial",
    link: RouteFactory.section("property/commercial-industrial"),
    label: "Commercial & Industrial",
  },
  "property-supplements": {
    key: "property-supplements",
    link: RouteFactory.propertySupplements,
    label: "Property Supplements",
  },
  "startups-tech": {
    key: "startups-tech",
    link: RouteFactory.section("startups-tech"),
    label: "Startups & Tech",
    children: [
      "startups-tech/startups",
      "startups-tech/technology",
      "startups-tech/tech-in-asia",
    ],
  },
  "startups-tech/startups": {
    key: "startups-tech/startups",
    link: RouteFactory.section("startups-tech/startups"),
    label: "Startups",
  },
  "startups-tech/technology": {
    key: "startups-tech/technology",
    link: RouteFactory.section("startups-tech/technology"),
    label: "Technology",
  },
  "startups-tech/tech-in-asia": {
    key: "startups-tech/tech-in-asia",
    link: "/tech-in-asia",
    label: "Tech in Asia",
  },
  esg: {
    key: "esg",
    link: RouteFactory.section("esg"),
    label: "ESG",
    children: [
      "esg/sustainable-finance",
      "esg/governance-diversity",
      "esg/giving-back",
    ],
  },
  "esg/sustainable-finance": {
    key: "esg/sustainable-finance",
    link: RouteFactory.section("esg/sustainable-finance"),
    label: "Sustainable Finance",
  },
  "esg/governance-diversity": {
    key: "esg/governance-diversity",
    link: RouteFactory.section("esg/governance-diversity"),
    label: "Governance & Diversity",
  },
  "esg/giving-back": {
    key: "esg/giving-back",
    link: RouteFactory.section("esg/giving-back"),
    label: "Giving Back",
  },
  wealth: {
    key: "wealth",
    link: RouteFactory.section("wealth"),
    label: "Wealth",
    children: [
      "wealth/wealth-investing",
      "wealth/personal-finance",
      "wealth/crypto-alternative-assets",
      "wealth/supplements",
    ],
  },
  "wealth/wealth-investing": {
    key: "wealth/wealth-investing",
    link: RouteFactory.section("wealth/wealth-investing"),
    label: "Wealth & Investing",
  },
  "wealth/personal-finance": {
    key: "wealth/personal-finance",
    link: RouteFactory.section("wealth/personal-finance"),
    label: "Personal Finance",
  },
  "wealth/crypto-alternative-assets": {
    key: "wealth/crypto-alternative-assets",
    link: RouteFactory.section("wealth/crypto-alternative-assets"),
    label: "Crypto & Alternative Assets",
  },
  "wealth/supplements": {
    key: "wealth/supplements",
    link: RouteFactory.section("wealth/supplements"),
    label: "Supplements",
  },
  "wealth/insurance": {
    key: "wealth/insurance",
    link: RouteFactory.wealthInsurance,
    label: "Insurance",
  },
  "working-life": {
    key: "working-life",
    link: RouteFactory.section("working-life"),
    label: "Working Life",
    children: ["working-life/lifes-work", "working-life/leadership"],
  },
  "working-life/lifes-work": {
    key: "working-life/lifes-work",
    link: RouteFactory.section("working-life/lifes-work"),
    label: "Life's Work",
  },
  "working-life/leadership": {
    key: "working-life/leadership",
    link: RouteFactory.section("working-life/leadership"),
    label: "Leadership",
  },
  lifestyle: {
    key: "lifestyle",
    link: RouteFactory.section("lifestyle"),
    label: "Lifestyle",
    children: [
      "lifestyle/food-drink",
      "lifestyle/motoring",
      "lifestyle/style-society",
      "lifestyle/watches-jewellery",
      "lifestyle/arts-design",
      "lifestyle/bt-luxe",
      "lifestyle/travel-wellness",
    ],
  },
  "lifestyle/food-drink": {
    key: "lifestyle/food-drink",
    link: RouteFactory.section("lifestyle/food-drink"),
    label: "Food & Drink",
  },
  "lifestyle/motoring": {
    key: "lifestyle/motoring",
    link: RouteFactory.section("lifestyle/motoring"),
    label: "Motoring",
  },
  "lifestyle/style-society": {
    key: "lifestyle/style-society",
    link: RouteFactory.section("lifestyle/style-society"),
    label: "Style & Society",
  },
  "lifestyle/watches-jewellery": {
    key: "lifestyle/watches-jewellery",
    link: RouteFactory.section("lifestyle/watches-jewellery"),
    label: "Watches & Jewellery",
  },
  "lifestyle/arts-design": {
    key: "lifestyle/arts-design",
    link: RouteFactory.section("lifestyle/arts-design"),
    label: "Arts & Design",
  },
  "lifestyle/bt-luxe": {
    key: "lifestyle/bt-luxe",
    link: RouteFactory.section("lifestyle/bt-luxe"),
    label: "BT Luxe",
  },
  "lifestyle/travel-wellness": {
    key: "lifestyle/travel-wellness",
    link: RouteFactory.section("lifestyle/travel-wellness"),
    label: "Travel & Wellness",
  },
  "bt-luxe": {
    key: "bt-luxe",
    link: RouteFactory.section("lifestyle/bt-luxe"),
    label: "BT Luxe",
  },
  "events-awards": {
    key: "events-awards",
    link: RouteFactory.eventsAwards,
    label: "Events & Awards",
    children: [
      "events-awards/singapore-business-awards",
      "events-awards/singapore-corporate-awards",
      "events-awards/enterprise-50",
      "events-awards/emerging-enterprise",
      "events-awards/sustainability-impact-awards",
      "events-awards/design-ai-and-tech-awards",
    ],
  },
  "events-awards/singapore-business-awards": {
    key: "events-awards/singapore-business-awards",
    link: RouteFactory.section("events-awards/singapore-business-awards"),
    label: "Singapore Business Awards",
  },
  "events-awards/singapore-corporate-awards": {
    key: "events-awards/singapore-corporate-awards",
    link: RouteFactory.section("events-awards/singapore-corporate-awards"),
    label: "Singapore Corporate Awards",
  },
  "events-awards/enterprise-50": {
    key: "events-awards/enterprise-50",
    link: RouteFactory.section("events-awards/enterprise-50"),
    label: "Enterprise 50",
  },
  "events-awards/emerging-enterprise": {
    key: "events-awards/emerging-enterprise",
    link: RouteFactory.section("events-awards/emerging-enterprise"),
    label: "Emerging Enterprise",
  },
  "events-awards/sustainability-impact-awards": {
    key: "events-awards/sustainability-impact-awards",
    link: RouteFactory.section("events-awards/sustainability-impact-awards"),
    label: "Sustainability Impact Awards",
  },
  "events-awards/design-ai-and-tech-awards": {
    key: "events-awards/design-ai-and-tech-awards",
    link: RouteFactory.section("events-awards/design-ai-and-tech-awards"),
    label: "Design AI and Tech Awards",
  },
  "breaking-news": {
    key: "breaking-news",
    link: RouteFactory.breakingNews,
    label: "Breaking News",
  },
  /**
   * epaper
   */
  epaper: {
    key: "epaper",
    link: `${EPAPER_API_ENDPOINT}`,
    label: "E-paper",
    target: "_blank",
  },
  /**
   * podcasts
   */
  podcasts: {
    key: "podcasts",
    link: RouteFactory.podcasts,
    label: "Podcasts",
  },
  "keywords/money-hacks": {
    key: "keywords/money-hacks",
    link: RouteFactory.keywords("money-hacks"),
    label: "Money Hacks",
  },
  "keywords/market-focus": {
    key: "keywords/market-focus",
    link: RouteFactory.keywords("market-focus"),
    label: "Market Focus",
  },
  "keywords/lens-on": {
    key: "keywords/lens-on",
    link: RouteFactory.keywords("lens-on"),
    label: "Lens On",
  },
  "keywords/wealthbt": {
    key: "keywords/wealthbt",
    link: RouteFactory.keywords("wealthbt"),
    label: "WealthBT",
  },
  "keywords/mark-to-market": {
    key: "keywords/mark-to-market",
    link: RouteFactory.keywords("mark-to-market"),
    label: "Mark to Market",
  },
  "keywords/branded-podcasts": {
    key: "keywords/branded-podcasts",
    link: RouteFactory.keywords("branded-podcasts"),
    label: "Branded Podcasts",
  },
  "keywords/propertybt": {
    key: "keywords/propertybt",
    link: RouteFactory.keywords("propertybt"),
    label: "PropertyBT",
  },
  "keywords/bt-correspondents": {
    key: "keywords/bt-correspondents",
    link: RouteFactory.keywords("bt-correspondents"),
    label: "BT Correspondents",
  },
  /**
   * videos
   */
  videos: {
    key: "videos",
    link: RouteFactory.videos,
    label: "Videos",
  },
  /**
   * newsletter
   */
  newsletter: {
    key: "newsletter",
    link: RouteFactory.newsletterSignup,
    label: "Newsletters",
  },
  btclub: {
    key: "btclub",
    link: "https://www.btclub.sg",
    label: "BT Club",
  },
  thrive: {
    key: "thrive",
    link: RouteFactory.thrive,
    label: "thrive",
    stickyHeaderLabel: "thrive",
    target: "_blank",
    children: [
      "thrive/money-talk",
      "thrive/personal-growth",
      "thrive/career-guide",
      "thrive/more-about-adulting",
      "thrive/about-us",
      "thrive/events",
    ],
  },
  "thrive/money-talk": {
    key: "thrive/money-talk",
    link: RouteFactory.thriveMoneyTalk,
    label: "Money",
  },
  "thrive/personal-growth": {
    key: "thrive/personal-growth",
    link: RouteFactory.thrivePersonalGrowth,
    label: "Growth",
  },
  "thrive/career-guide": {
    key: "thrive/career-guide",
    link: RouteFactory.thriveCareerGuide,
    label: "Career",
  },
  "thrive/more-about-adulting": {
    key: "thrive/more-about-adulting",
    link: "/thrive/more-about-adulting",
    label: "Adulting",
  },
  "thrive/about-us": {
    key: "thrive/about-us",
    link: RouteFactory.thriveAboutUs,
    label: "About",
  },
  "thrive/events": {
    key: "thrive/events",
    link: RouteFactory.thriveEvents,
    label: "Events",
  },
  // others
  business: {
    key: "business",
    link: RouteFactory.section("business"),
    label: "Business",
  },
  stocks: {
    key: "stocks",
    link: RouteFactory.section("stocks"),
    label: "Stocks",
  },
  finance: {
    key: "finance",
    link: RouteFactory.section("finance"),
    label: "Finance",
  },
  "real-estate": {
    key: "real-estate",
    link: RouteFactory.section("real-estate"),
    label: "Real Estate",
  },
  "energy-commodities": {
    key: "energy-commodities",
    link: RouteFactory.section("energy-commodities"),
    label: "Energy & Commodities",
  },
  focus: {
    key: "focus",
    link: RouteFactory.section("focus"),
    label: "Focus",
  },
  "government-economy": {
    key: "government-economy",
    link: RouteFactory.section("government-economy"),
    label: "Government & Economy",
  },
  brunch: {
    key: "brunch",
    link: RouteFactory.section("brunch"),
    label: "Brunch",
  },
  consumer: {
    key: "consumer",
    link: RouteFactory.section("consumer"),
    label: "Consumer",
  },
  "lifestyle/podcasts": {
    key: "lifestyle/podcasts",
    link: RouteFactory.section("lifestyle/podcasts"),
    label: "Podcasts",
  },
  jr: {
    key: "jr",
    link: RouteFactory.section("jr"),
    label: "E-PAPER",
  },
  "singapore-budget-2024": {
    key: "singapore-budget-2024",
    link: RouteFactory.budget2024,
    label: "Singapore Budget 2024",
  },
  "keywords/singapore-budget-2024": {
    key: "singapore-budget-2024",
    link: RouteFactory.keywordsBudget2024,
    label: "Singapore Budget 2024",
  },
  "keywords/singapore-budget-2025": {
    key: "singapore-budget-2025",
    link: RouteFactory.keywordsBudget2025,
    label: "Budget 2025",
  },
  "keywords/singapore-budget-2026": {
    key: "singapore-budget-2026",
    link: RouteFactory.keywordsBudget2026,
    label: "Budget 2026",
  },
  "keywords/ge2025": {
    key: "ge2025",
    link: RouteFactory.keywordsSingaporeGe2025,
    label: "GE 2025",
  },
  "paid-press-release": {
    key: "paid-press-release",
    link: RouteFactory.paidPressRelease,
    label: "Paid Press Releases",
  },
  advertise: {
    key: "advertise",
    link: RouteFactory.advertise,
    label: "Advertise",
  },
  hub: {
    key: "hub",
    link: RouteFactory.hub,
    label: "Hub",
  },
  newsletters: {
    key: "newsletters",
    link: RouteFactory.newsletters,
    label: "Newsletters",
    stickyHeaderLabel: "Newsletters",
    children: [
      "newsletters/daily",
      "newsletters/asean-business",
      "lifestyle",
      "newsletters/property-insights",
      "newsletters/esg-insights",
      "newsletters/big-money",
      "singapore/smes",
      "thrive",
    ],
  },
  "newsletters/asean-business": {
    key: "newsletters/asean-business",
    link: RouteFactory.newslettersAseanBusiness,
    label: "Asean Business",
  },
  "newsletters/daily": {
    key: "newsletters/daily",
    link: RouteFactory.newslettersDaily,
    label: "Daily Newsletters",
  },
  "newsletters/big-money": {
    key: "newsletters/big-money",
    link: RouteFactory.newslettersBigMoney,
    label: "Big Money",
  },
  "newsletters/thrive": {
    key: "newsletters/thrive",
    link: RouteFactory.newslettersThrive,
    label: "Thrive",
  },
  "newsletters/lifestyle": {
    key: "newsletters/lifestyle",
    link: RouteFactory.newslettersLifestyle,
    label: "Lifestyle",
  },
  "newsletters/property-insights": {
    key: "newsletters/property-insights",
    link: RouteFactory.newslettersPropertyInsights,
    label: "Property Insights",
  },
  "newsletters/esg-insights": {
    key: "newsletters/esg-insights",
    link: RouteFactory.newslettersEsgInsights,
    label: "ESG Insights",
  },
  "newsletters/sgsme": {
    key: "newsletters/sgsme",
    link: RouteFactory.newslettersSgsme,
    label: "SGSME",
  },
  "keywords/bt-sg60": {
    key: "keywords/bt-sg60",
    link: RouteFactory.btsg60,
    label: "BT SG60",
  },
};

export const eventsAwardsRoutesKey = [
  sectionNavigationItems["events-awards"]?.key,
  sectionNavigationItems["events-awards/enterprise-50"]?.key,
  sectionNavigationItems["events-awards/emerging-enterprise"]?.key,
  sectionNavigationItems["events-awards/singapore-business-awards"]?.key,
  sectionNavigationItems["events-awards/singapore-corporate-awards"]?.key,
  sectionNavigationItems["events-awards/sustainability-impact-awards"]?.key,
  sectionNavigationItems["events-awards/design-ai-and-tech-awards"]?.key,
];

/**
 * All keywords path in navigation
 */
const allKeywordsPathInNavigation = Object.keys(sectionNavigationItems).filter(
  (key) => key.includes("keywords/")
);

/**
 * These items have specific data loaders and layouts that
 * are not compatible with the default section page.
 * They are excluded from the router (section page routes).
 */
export const sectionNavigationItemsExcludedInRouter = [
  sectionNavigationItems.myBT?.key,
  sectionNavigationItems["breaking-news"]?.key,
  sectionNavigationItems["singapore-budget-2024"]?.key,
  sectionNavigationItems["podcasts"]?.key,
  sectionNavigationItems["keywords/money-hacks"]?.key,
  sectionNavigationItems["keywords/market-focus"]?.key,
  sectionNavigationItems["keywords/lens-on"]?.key,
  sectionNavigationItems["keywords/wealthbt"]?.key,
  sectionNavigationItems["keywords/propertybt"]?.key,
  sectionNavigationItems["keywords/mark-to-market"]?.key,
  sectionNavigationItems["keywords/branded-podcasts"]?.key,
  sectionNavigationItems["keywords/bt-correspondents"]?.key,
  sectionNavigationItems["wealth/supplements"]?.key,
  sectionNavigationItems["property-supplements"]?.key,
  sectionNavigationItems["videos"]?.key,
  ...eventsAwardsRoutesKey,
  ...allKeywordsPathInNavigation,
];

export const SEARCH_FILTER_KEYS = {
  date: {
    key: "date",
    label: "date",
  },
  category: {
    key: "category",
    label: "category",
  },
  author: {
    key: "author",
    label: "author",
  },
};

export const SEARCH_FILTER_OPTIONS = {
  [SEARCH_FILTER_KEYS.date.key]: [
    {
      key: "1d",
    },
    {
      key: "1w",
    },
    {
      key: "1m",
    },
    {
      key: "6m",
    },
    {
      key: "1y",
    },
    {
      key: "3y",
    },
  ],
  [SEARCH_FILTER_KEYS.category.key]: [
    {
      key: "Companies & Markets",
    },
    {
      key: "International",
    },
    {
      key: "Capital Markets & Currencies",
    },
    {
      key: "Lifestyle",
    },
    {
      key: "Property",
    },
    {
      key: "Banking & Finance",
    },
    {
      key: "Transport & Logistics",
    },
    {
      key: "Economy & Policy",
    },
    {
      key: "Columns",
    },
    {
      key: "Energy & Commodities",
    },
    {
      key: "Garage",
    },
    {
      key: "Technology",
    },
    {
      key: "Top Stories",
    },
    {
      key: "SGSME",
    },
    {
      key: "Hub",
    },
    {
      key: "Consumer & Healthcare",
    },
    {
      key: "Wealth & Investing",
    },
    {
      key: "ASEAN Business",
    },
    {
      key: "Features",
    },
    {
      key: "Singapore",
    },
  ],
  [SEARCH_FILTER_KEYS.author.key]: [
    {
      key: "Nisha Ramchandani",
    },
    {
      key: "Rachel Mui",
    },
    {
      key: "Lee Meixian",
    },
    {
      key: "Vivienne Tay",
    },
    {
      key: "Jamie Lee",
    },
    {
      key: "Annabeth Leow",
    },
    {
      key: "Fiona Lam",
    },
    {
      key: "Lynette Khoo",
    },
    {
      key: "Kenneth Lim",
    },
    {
      key: "Anita Gabriel",
    },
    {
      key: "Janice Heng",
    },
    {
      key: "Navin Sregantan",
    },
    {
      key: "Vivien Shiao",
    },
    {
      key: "Claire Huang",
    },
    {
      key: "Andrea Soh",
    },
    {
      key: "Jacquelyn Cheok",
    },
    {
      key: "Angela Tan",
    },
    {
      key: "Lee U-Wen",
    },
    {
      key: "Kalpana Rashiwala",
    },
    {
      key: "Tan Hwee Hwee",
    },
  ],
};

export const SEARCH_SORTERS = [
  { key: "relevant", label: "Relevant" },
  { key: "latest", label: "Newest" },
];

export const SCREEN_XL = 1200;
export const SCREEN_LG = 992;
export const SCREEN_MD = 600;
export const SCREEN_SM = 425;
export const SCREEN_XS = 375;

export const QUERYLY_SCRIPT = "https://www.queryly.com/js/queryly.v4.js";
export const QUERYLY_ADVANCE_SCRIPT =
  "https://www.queryly.com/js/businesstimes-advanced-search.js";

/**
 * paywallCharLength defines number of characters after which to truncate the content.
 * storyCharLength defines the range of total number of characters of paragraphs.
 * These values are picked from the BT production configuration.
 *  */

export const PAYWALL_TREATMENTS_TRUNCATE_LENGTHS = [
  { paywallCharLength: 300, storyCharLength: [0, 2500] },
  { paywallCharLength: 500, storyCharLength: [2500, 4000] },
  { paywallCharLength: 700, storyCharLength: [4000, 100000] },
];

/**
 * VERTICAL_COLOUR_VARIANTS is a map for the different vertical colour themes
 * You may add new vertical names to VerticalName e.g. "btblue"
 * You may add new styles to VerticalColourVariant e.g. "hoverStyle", "shadowStyle"
 * @see https://tailwindcss.com/docs/content-configuration#dynamic-class-names
 *  */

export const VERTICAL_COLOUR_VARIANTS: Record<
  VerticalName,
  VerticalColourVariant
> = {
  asean: {
    textStyle: "text-verticals-asean",
    backgroundStyle: "bg-verticals-asean",
    linkStyle: "text-verticals-asean visited:text-verticals-asean",
    hoverStyle: "hover:text-verticals-asean-hover",
  },
  sme: {
    textStyle: "text-verticals-sme",
    backgroundStyle: "bg-verticals-sme",
    linkStyle: "text-verticals-sme visited:text-verticals-sme",
    hoverStyle: "hover:text-verticals-sme-hover",
  },
  ge: {
    textStyle: "text-verticals-ge",
    backgroundStyle: "bg-verticals-ge",
    linkStyle:
      "text-verticals-ge visited:text-verticals-ge-hover hover:text-verticals-ge-hover focus:text-verticals-ge-hover",
    hoverStyle: "hover:text-verticals-ge-hover",
  },
  garage: {
    textStyle: "text-verticals-garage",
    backgroundStyle: "bg-verticals-garage",
    linkStyle:
      "text-verticals-garage visited:text-verticals-garage-hover hover:text-verticals-garage-hover focus:text-verticals-garage-hover",
    hoverStyle: "hover:text-verticals-garage-hover",
  },
  esg: {
    textStyle: "!text-black",
    backgroundStyle: "bg-yellow",
    linkStyle:
      "text-verticals-btblue visited:text-gray-850 hover:text-gray-850 focus:text-gray-850",
  },
};

export type VerticalColourVariant = {
  textStyle: string;
  backgroundStyle: string;
  linkStyle: string;
  hoverStyle?: string;
};

export type VerticalName = "asean" | "sme" | "ge" | "garage" | "esg";

/**
 * Available timestamp formats used in BT
 */
export const defaultTimestampFormat = "MMM DD, YYYY hh:mm A";
export const articleTimestampFormat = "ddd, MMM D, YYYY · hh:mm A";
export const shortTimestampFormat = "hh:mm A";
export const stockWidgetTimestampFormat = "DD MMM YYYY hh:mm A";
export const searchDialogTimestampFormat = "ddd, MMMM DD, YYYY [At] hh:mm A";
export const articleDateOnlyTimestampFormat = "ddd, MMM D, YYYY";

export const dayjsSingaporeTimezone = "Asia/Singapore";

export const isCIorLocal = import.meta.env.VITE_CI || ENVIRONMENT === "local";

export const EMARSYS_API_BASE_URL = "https://api.emarsys.net";
export const EMARSYS_API_USERNAME = import.meta.env.VITE_EMARSYS_API_USERNAME;
export const EMARSYS_API_PASSWORD = import.meta.env.VITE_EMARSYS_API_PASSWORD;

/**
 * Constants used for Gift an Article flow
 */
export const URL_PROMOTIONS_BT_PERSONAL =
  "https://subscribe.sph.com.sg/promotions/bt-personal/?utm_campaign=btp99&utm_medium=sph-publication&utm_source=subshare-bt&utm_content=subscribebutton-expiredlink-pay";
export const URL_PUBLICATIONS_BT =
  "https://subscribe.sph.com.sg/publications-bt/?utm_campaign=bt_subscription&utm_medium=sph-publication&utm_source=subshare-bt&utm_content=subscribebutton-expiredlink-pay";

export const ARTICLE_INIFINITE_SCROLL_LIMIT = 15;
export const STORIES_PAGE_SIZE = 10;
export const TRUNCATE_LENGTH = 22;

export const configSite = {
  name: "The Business Times",
  address:
    import.meta.env.VITE_PUBLIC_BASE_URL || "https://www.businesstimes.com.sg", // without trailing slash
};

export const PUBLIC_PREVIEW_EXCLUDE_FROM_HOME = "exclude_from_home";
export const PUBLIC_PREVIEW_MUST_NOT_CLAUSE = {
  match_phrase: {
    "data.context.displaySetting.displayAssetsMultiple":
      PUBLIC_PREVIEW_EXCLUDE_FROM_HOME,
  },
};

export const DEFAULT_CACHE_CONTROL =
  "max-age=30, stale-while-revalidate=30, stale-if-error=86400";
export const SITEMAP_CACHE_CONTROL =
  "max-age=86400, stale-while-revalidate=86400, stale-if-error=172800"; // 24 hours fresh, revalidate, and 48 hours stale if error
export const FEEDS_CACHE_CONTROL =
  "max-age=600, stale-while-revalidate=300, stale-if-error=86400";

export const API_CACHE_CONTROL =
  "max-age=600, stale-while-revalidate=600, stale-if-error=86400"; // 10 minutes cache for API GET endpoints

export const API_CACHE_CONTROL_THREE_HOURS =
  "max-age=10800, stale-while-revalidate=600, stale-if-error=86400"; // 3 hours cache for most popular API GET endpoint

export const API_CACHE_CONTROL_THREE_MINUTES =
  "max-age=180, stale-while-revalidate=180, stale-if-error=86400";

export const STORY_TYPE_FILTER = {
  bool: {
    minimum_should_match: 1,
    should: [
      "breakingNews",
      "onlineStory",
      "featureStory",
      "galleryStory",
      "videoStory",
    ].map((type) => ({
      match: {
        "data.context.type": type,
      },
    })),
  },
};

export const ROUTES_TO_EXCLUDE_COMMON_FOOTER = [
  RouteFactory.myBTOnboard,
  RouteFactory.mbo,
  RouteFactory.pulse,
  RouteFactory.asiaSquare,
];

export const OS_INDEX_ALIAS = "businesstimes";
export const OS_ENRICHED_INDEX_ALIAS = "caas-enrichment-bt";
export const OS_INDEX_STORY_QUEUE_ALIAS = "sph_storyqueue";

export const OS_AUTHOR_ALIAS = "sph_author_contributor";

export const whatsappChannelUrl = "https://bit.ly/4bwNj8B";

export const BigMoneyNewsletter = {
  emarsysId: "63311",
  premiumEmailValidation:
    "Please use your e-mail address that’s linked to your BT account. Not a BT subscriber? Pick your plan ",
  subscriptionURl:
    "https://subscribe.sph.com.sg/promotions/bt-personal/?utm_campaign=bigmoneynewsletter&utm_medium=sph-publication&utm_source=bt&utm_content=Newsletterlistingpage-Bigmoneytile",
};

export function toRSSDateLocal(formatdate?: string): string {
  const date = formatdate ? dayjs(formatdate) : dayjs();

  return date.format("ddd, DD MMM YYYY HH:mm:ss ZZ");
}
export function toRSSDateUTC(formatdate?: string): string {
  const date = formatdate ? dayjs(formatdate) : dayjs();

  return date.utcOffset(8).format("YYYY-MM-DDTHH:mm:ssZ");
}
export const SECTION_UNIQUE_PATH = (uniqueName: string) => {
  // Lets build our section path. By default section un coming from
  // caas split using _ instead of /. For e.g fashion_news-trends
  return "/" + uniqueName.split("_").join("/");
};
export const APP_ADS_TEXT_ORIGIN =
  "https://adtag.sphdigital.com/tag/ads/app-ads.txt";
export const ADS_TEXT_ORIGIN =
  "https://adtag.sphdigital.com/tag/ads/ads_bt.txt";
export const SERVER_ERROR_CACHE_CONTROL = "max-age=30";
// We do have articles from 2013 September onwards.
export const SITEMAP_STARTING_YEAR = 2013;
export const SITEMAP_STARTING_MONTH = 9;

export const MYSPH_JS_BUNDLE_PATH = import.meta.env.VITE_MYSPH_JS_BUNDLE_PATH;
export const MYSPH_CLIENT_ID = import.meta.env.VITE_MYSPH_CLIENT_ID;
export const ASIA_FUTURE_SUMMIT_URL = "/keywords/asia-future-summit";

export const ZH_HANS_SITEMAP_STARTING_YEAR = 2025;
export const ZH_HANS_SITEMAP_STARTING_MONTH = 9;

export const REGENERATE_INTERVAL_FOR_SUCCESSFULLY_RENDERED_PAGES = 604800; // 1 week

export const REGENERATE_INTERVAL_3_HOURS = 10800; // 3 hours

export const WHATSAPP_BANNER_URL =
  "https://www.whatsapp.com/channel/0029VaI0vLF29753DedKkb24";

export const REGENERATE_INTERVAL_10_MINUTES = 600;

export const LINKIDIN_URL_SCA =
  "https://www.linkedin.com/company/14614358/admin/";

export const STANDARD_CHARTERED_URL = "https://www.sc.com/sg";

export const ACRA_URL = "https://www.acra.gov.sg";

export const SGX_URL = "https://www.sgx.com";

export const NUS_URL = "https://bschool.nus.edu.sg/cgs/";

export const DELOITTE_URL = "https://www.deloitte.com";

export const EY_URL = "https://ey.com";

export const HEDRICK_URL = "https://www.heidrick.com";

export const KPMG_URL = "https://www.kpmg.com.sg";

export const PWC_URL = "https://www.pwc.com/sg";

export const SID_URL = "https://www.sid.org.sg";

export const BUSINESS_URL = "https://www.businesstimes.com.sg";

export const CITYGATE_URL = "https://cdrconsultancy.com/#about-us";

export const THRIVE_INSTAGRAM_URL = "https://www.instagram.com/thrive_bt/";

export const THRIVE_TIKTOK_URL = "https://www.tiktok.com/@thrivebt";

export const ENTERPRISE_50_URL = "https://www.enterprise50.org";

export const EMERGING_ENTERPRISE_FORM =
  "https://graphics.businesstimes.com.sg/bt_files/awards/document/ee_awards_2025_form.pdf";

export const OCBC_BANKING_URL = "https://www.ocbc.com.sg/business-banking";

export const RSM_URL = "https://www.rsmsingapore.sg/";

export const MASTERCARD_URL = "https://www.mastercard.com/";

export const EMERGING_ENTERPRISE = "https://www.enterprisesg.gov.sg/";

export const ASTAR_URL = "https://www.a-star.edu.sg/";

export const OLIVER_WYMAN_URL = "https://www.oliverwyman.com/";

export const MARSH_MCLENNAN_URL = "https://www.marshmclennan.com/";

export const PAIA_URL = "https://paiaconsulting.com.sg/";

export const DTA_SUBMISSIONS_URL =
  "https://graphics.businesstimes.com.sg/bt_files/awards/document/daita_2026_application_form.pdf";

export const GOOGLE_PREFERED_SOURCES_LINK =
  "https://www.google.com/preferences/source?q=https://www.businesstimes.com.sg";

export const SCA_NOMINATIONS_URL = "https://form.jotform.com/260282691502050";

export const SIA_INDIVIDUAL_NOMINATIONS_URL =
  "https://static1.businesstimes.com.sg/s3fs-public/2026-03/_final_si_awards_2026_application_form_individual.pdf";

export const SIA_ENTERPRISE_NOMINATIONS_URL =
  "https://static1.businesstimes.com.sg/s3fs-public/2026-03/_final_si_awards_2026_application_form_enterprise.pdf";

export const E50_FORM_URL =
  "https://forms.office.com/pages/responsepage.aspx?id=uyT_3okgAESMjvceaAN4srgYqF1_ge1Lqyvz-2sG0z9UMUZPUUk0M0xUNEs0VVpHM1o4WExUTEFSRC4u&route=shorturl";
